import { useMemo, useCallback } from "react";
import { useGetMe } from "@workspace/api-client-react";

/**
 * Frontend RBAC helper. Mirrors the backend `requirePermission(module, action)`
 * model: `/api/me` returns a flat list of `"<module>:<action>"` permission
 * strings plus an `isSuperAdmin` flag. A user may see a feature if they are a
 * super admin or hold ANY action on at least one of the feature's modules.
 */
export function usePermissions() {
  const { data: me } = useGetMe();

  const permissions = useMemo<string[]>(
    () => {
      const raw = (me as unknown as { permissions?: unknown } | undefined)?.permissions;
      return Array.isArray(raw) ? raw.filter((p): p is string => typeof p === "string") : [];
    },
    [me],
  );
  const isSuperAdmin =
    (me as unknown as { isSuperAdmin?: boolean } | undefined)?.isSuperAdmin === true;

  const userModules = useMemo(
    () => new Set(permissions.filter((p): p is string => typeof p === "string").map((p) => p.split(":")[0])),
    [permissions],
  );

  // `modules == null` → always visible (e.g. dashboard, self-service portal).
  const canSee = useCallback(
    (modules?: string[] | null) => {
      if (modules == null) return true;
      if (isSuperAdmin) return true;
      return modules.some((m) => userModules.has(m));
    },
    [isSuperAdmin, userModules],
  );

  // Fine-grained check: does the user hold a specific `<module>:<action>`?
  // Mirrors the backend `requirePermission(module, action)`. Super admins pass.
  const can = useCallback(
    (module: string, action: string) =>
      isSuperAdmin || (Array.isArray(permissions) && permissions.includes(`${module}:${action}`)),
    [isSuperAdmin, permissions],
  );

  const roleName = ((me as unknown as { roleName?: string | null })?.roleName ?? "").toLowerCase();

  const isExecutive = useMemo(() => {
    if (isSuperAdmin) return true;
    const executiveRoles = [
      "ceo",
      "executive director",
      "managing director",
      "chief executive officer",
      "admin",
      "super admin",
      "director",
      "founder",
      "co-founder",
      "president",
      "vice president",
      "vp",
      "c-level",
      "cxo",
      "general manager",
    ];
    if (executiveRoles.some((r) => roleName === r || roleName.includes(r))) return true;
    return (
      permissions.includes("reports:executive:read") ||
      permissions.includes("reports:view") ||
      permissions.includes("executive:read") ||
      permissions.includes("*:*")
    );
  }, [isSuperAdmin, roleName, permissions]);

  return { canSee, can, isSuperAdmin, isExecutive, roleName, permissions, userModules, me };
}

